import { GameIcon, StatIcon } from "../assets/gameIcons.js";
import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect } from "react";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { Button } from "../ui/Button.js";
import { formatCompact } from "../ui/Counter.js";
import { Timer } from "../ui/Timer.js";
import { arenaApi } from "../units/api.js";
import { useUnits } from "../units/state.js";
import { Panel } from "../ui/Panel.js";

/** Arena Terminal (R35): rating band, attempts, opponents, defence log, season and top board. */
export function ArenaView() {
  const t = useT();
  const arena = useUnits((s) => s.arena);
  const log = useUnits((s) => s.arenaLog);
  const refreshArena = useUnits((s) => s.refreshArena);
  const run = useUnits((s) => s.run);
  const setResult = useUnits((s) => s.setResult);
  useEffect(() => {
    void refreshArena();
  }, [refreshArena]);
  if (!arena) return <p className="dim" style={{ padding: 16 }}>{t("common.loading")}</p>;

  const attack = async (opponentId: string) => {
    const res = await run(() => arenaApi.attack(opponentId));
    if (res) {
      setResult({ outcome: res, context: "arena", extra: { ratingDelta: res.ratingDelta, rating: res.rating, coins: res.coins } });
      void refreshArena();
    }
  };
  const refresh = async () => {
    if (await run(() => arenaApi.refresh(), t("arena.toast.refreshed"))) void refreshArena();
  };
  const refill = async () => {
    if (await run(() => arenaApi.refill(), t("arena.toast.refilled"))) void refreshArena();
  };

  return (
    <div className="page" data-testid="arena-view">
      <Panel title={t("arena.title")}>
        <div className="row" style={{ gap: 12, flexWrap: "wrap" }}>
          <div className="band-frame" style={{ borderColor: arena.band.color, color: arena.band.color }} data-testid="arena-band">
            <div style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>{pick(arena.band.name)}</div>
            <div className="mono" style={{ fontSize: 26, fontWeight: 800 }} data-testid="arena-rating">
              {arena.rating}
            </div>
          </div>
          <div className="grow">
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <span className="badge gold" data-testid="arena-attempts">
                {t("arena.attempts")} {arena.attempts.max - arena.attempts.used} / {arena.attempts.max}
              </span>
              <span className="badge">ARC {arena.coins}</span>
              <span className="badge ok">{arena.wins} W</span>
              <span className="badge warn">{arena.losses} L</span>
            </div>
            <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <Button variant="gold" style={{ minHeight: 34 }} onClick={() => void refresh()} data-testid="arena-refresh">
                <FunctionalIcon name="refresh" /> {t("arena.refresh")} · {arena.refreshCredits} ₵
              </Button>
              <Button variant="gold" style={{ minHeight: 34 }} disabled={arena.attempts.used === 0} onClick={() => void refill()} data-testid="arena-refill">
                +1 {t("arena.attempt")} · {arena.attempts.refillCredits} ₵
              </Button>
              <Button style={{ minHeight: 34 }} onClick={() => navigate("/app/units/squads")}>
                {t("arena.defence", { n: arena.defenceSlot })}
              </Button>
            </div>
          </div>
        </div>
      </Panel>

      <Panel title={t("arena.opponents")}>
        {arena.opponents.length === 0 ? <p className="dim">{t("arena.noOpponents")}</p> : null}
        {arena.opponents.map((o) => (
          <div key={o.accountId} className="row list-row" data-testid={`opponent-${o.accountId}`}>
            <span className="grow">
              <strong>{o.name}</strong>
              <div className="hint">
                {o.rating} · {o.band} · <StatIcon stat="power" /> {formatCompact(o.power)}
              </div>
            </span>
            {o.cooldownUntil ? (
              <span className="badge">
                <FunctionalIcon name="clock" /> <Timer finishesAt={o.cooldownUntil} />
              </span>
            ) : (
              <Button variant="primary" style={{ minHeight: 34 }} disabled={arena.attempts.used >= arena.attempts.max} onClick={() => void attack(o.accountId)} data-testid={`attack-${o.accountId}`}>
                <FunctionalIcon name="attack" /> {t("arena.attack")}
              </Button>
            )}
          </div>
        ))}
      </Panel>

      <Panel title={t("arena.season")}>
        <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
          <span className="badge gold">#{arena.season.rank}</span>
          <span className="hint">{arena.season.weekKey}</span>
          <span className="hint">
            {t("arena.endsIn")} <Timer finishesAt={arena.season.endsAt} />
          </span>
          <span className="mono hint">
            ARC {arena.season.reward.coins} · CDC {arena.season.reward.cdc} · FAB {arena.season.reward.fab}
          </span>
        </div>
        <h3>{t("arena.top")}</h3>
        {arena.top.slice(0, 10).map((p, i) => (
          <div key={p.accountId} className="row" style={{ fontSize: 13, padding: "2px 0" }}>
            <span className="mono" style={{ width: 28 }}>
              {i + 1}.
            </span>
            <span className="grow ellipsis">{p.name}</span>
            <span className="mono">{p.rating}</span>
          </div>
        ))}
      </Panel>

      <Panel title={t("arena.log")}>
        {(log ?? []).length === 0 ? <p className="dim">{t("arena.noLog")}</p> : null}
        {(log ?? []).map((e) => (
          <div key={e.id} className="row list-row" data-testid="arena-log-entry">
            <span className={`badge ${e.won ? "ok" : "warn"}`}>{e.won ? "W" : "L"}</span>
            <span className="grow">
              <GameIcon id={e.attacker ? "ui.role.attacker" : "emote.shield"} /> 
              {e.opponentName}
              <div className="hint">{new Date(e.createdAt).toLocaleString()}</div>
            </span>
            <span className="mono">
              {e.ratingDelta >= 0 ? "+" : ""}
              {e.ratingDelta}
            </span>
            <Button style={{ minHeight: 30 }} onClick={() => navigate(`/app/battle/${e.battleId}`)} data-testid="arena-log-replay">
              <FunctionalIcon name="play" />
            </Button>
            {!e.attacker && !e.won ? (
              <Button style={{ minHeight: 30 }} onClick={() => void attack(e.opponentId)} data-testid="arena-revenge">
                {t("arena.revenge")}
              </Button>
            ) : null}
          </div>
        ))}
      </Panel>
    </div>
  );
}
